import { ChangeDetectionStrategy, Component, computed, signal, inject } from '@angular/core';
import { NavigationService } from '../../services/navigation.service';
import { UserService } from '../../services/user.service';
import { MonthService } from '../../services/month.service';
import { Router, RouterLink } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { map, shareReplay } from 'rxjs/operators';
import { DrawerService } from '../../services/drawer.service';
import { AsyncPipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { UiButtonDirective } from '../../ui/button/button.directive';
import {
  UiPrimaryNavigationDirective,
  UiPrimaryNavigationItemDirective,
} from '../../ui/primary-navigation/primary-navigation.directive';
import { MonthSelectorComponent } from '../month-selector/month-selector.component';
import {
  UiDropdownMenuTriggerDirective,
  UiDropdownMenuDirective,
  UiDropdownMenuItemDirective,
} from '../../ui/menu/menu.directive';
import { SupportComponent } from '../support/support.component';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { HeaderCountBadgeComponent } from './header-count-badge.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  imports: [
    RouterLink,
    UiIconComponent,
    UiButtonDirective,
    UiPrimaryNavigationDirective,
    UiPrimaryNavigationItemDirective,
    MonthSelectorComponent,
    HeaderCountBadgeComponent,
    UiDropdownMenuTriggerDirective,
    UiDropdownMenuDirective,
    UiDropdownMenuItemDirective,
    SupportComponent,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  nav = inject(NavigationService);
  userService = inject(UserService);
  sessionService = inject(SessionService);
  month = inject(MonthService);
  private router = inject(Router);
  private drawerService = inject(DrawerService);

  private readonly logoutMutation = injectMutation(() => this.userService.logoutMutationOptions());
  readonly monthSelectorTakeoverOpen = signal(false);
  readonly activeNavTab = computed(() => this.toActiveNavTab(this.nav.currentRouteSignal()));
  readonly reportsEnabled = toSignal(this.sessionService.hasEnabledFeature$('reports'), { initialValue: false });
  readonly impersonating$ = this.sessionService.session$.pipe(
    map(s => s && s.impersonatorId),
    shareReplay(1),
  );
  readonly unsortedTransactionsCount = toSignal(this.sessionService.unsortedTransactionsCount$, { initialValue: 0 });
  readonly deauthenticatedCredentialsCount = toSignal(this.sessionService.deauthenticatedCredentialsCount$, {
    initialValue: 0,
  });
  readonly transactionsAriaLabel = computed(() => {
    const count = this.unsortedTransactionsCount();
    if (!count) return 'Sort transactions';

    return `Sort transactions, ${count} uncategorized ${count === 1 ? 'transaction' : 'transactions'}`;
  });
  readonly accountsAriaLabel = computed(() => {
    const count = this.deauthenticatedCredentialsCount();
    if (!count) return 'View accounts';

    return `View accounts, ${count} bank ${count === 1 ? 'connection needs' : 'connections need'} attention`;
  });
  readonly initials$ = this.userService.user$.pipe(
    map(user => (user ? user.firstName.charAt(0) + user.lastName.charAt(0) : '')),
  );

  get currentMonthYear$() {
    return this.month.currentMonthYear$;
  }

  // Pages outside the tabs (recurring, rules, settings, upgrade) highlight none, so the
  // header never claims a tab you are not on; their back links carry the parent instead.
  private toActiveNavTab(
    currentRoute: string | null | undefined,
  ): 'budgets' | 'transactions' | 'reports' | 'accounts' | null {
    const path = currentRoute && currentRoute !== '/' ? currentRoute : '/budgets';
    if (path.startsWith('/budgets')) return 'budgets';
    if (path.startsWith('/transactions')) return 'transactions';
    if (path.startsWith('/reports')) return 'reports';
    if (path.startsWith('/accounts')) return 'accounts';
    return null;
  }

  navigateTo(location: string[]): void {
    this.drawerService.closeMobileSheet();
    this.nav.navigateTo(location);
  }

  navigateToTransactions(unsortedTransactionsCount: number | null | undefined) {
    this.drawerService.closeMobileSheet();
    return this.router.navigate(['/transactions'], {
      queryParams: unsortedTransactionsCount && unsortedTransactionsCount > 0 ? { uncategorized: '1' } : {},
    });
  }

  logout() {
    this.logoutMutation.mutate();
  }
}
